/**
 * Coalescing rules for SystemContext.fetchSystemData.
 *
 * A system-data refresh downloads and re-persists the whole catalog (foods,
 * categories, VIP cards, bookings, admins), re-parses it from storage and
 * pushes it through the app-wide SystemContext, re-rendering every consumer.
 * Several screens trigger it automatically (HomeScreen on every params change,
 * SuccessPaymentScreen and the success-modal close after each payment, ...),
 * so a single payment used to run up to three full syncs back to back and
 * freeze the UI on low-end Android devices.
 */

/** Automatic refreshes within this window of the last completed one are skipped. */
export const SYSTEM_DATA_MIN_INTERVAL_MS = 30 * 1000;

/** Catalogs with more foods than this are "large" for throttling purposes. */
export const LARGE_CATALOG_FOOD_COUNT = 3000;

/** Automatic-refresh window for large catalogs (a sync is hundreds of page requests). */
export const SYSTEM_DATA_LARGE_CATALOG_MIN_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Automatic-refresh window for the given cached catalog size. A store with tens
 * of thousands of products must not re-download the whole catalog after every
 * payment; explicit refreshes (`force`) still go through immediately.
 *
 * @param {number} foodCount Number of foods currently cached.
 * @returns {number} Minimum ms between automatic refreshes.
 */
export const getSystemDataMinInterval = (foodCount) => (
    Number(foodCount) > LARGE_CATALOG_FOOD_COUNT
        ? SYSTEM_DATA_LARGE_CATALOG_MIN_INTERVAL_MS
        : SYSTEM_DATA_MIN_INTERVAL_MS
);

/**
 * Decide whether a non-forced fetchSystemData call can be skipped.
 *
 * @param {object} params
 * @param {boolean} [params.force] Explicit user refresh / data change: never skip.
 * @param {number|null} params.lastFetchAt Epoch ms the last fetch completed (null = never).
 * @param {string|null} params.lastScopeKey Store/place scope of the last fetch.
 * @param {string|null} params.scopeKey Current store/place scope.
 * @param {number} params.now Current epoch ms.
 * @param {number} [params.minIntervalMs=SYSTEM_DATA_MIN_INTERVAL_MS]
 * @returns {boolean} `true` when the call should be skipped.
 */
export const shouldSkipSystemDataFetch = ({
    force = false,
    lastFetchAt,
    lastScopeKey,
    scopeKey,
    now,
    minIntervalMs = SYSTEM_DATA_MIN_INTERVAL_MS,
}) => {
    if (force || !Number.isFinite(lastFetchAt)) {
        return false;
    }

    // A place/store switch must always load the new scope's catalog.
    if (!scopeKey || scopeKey !== lastScopeKey) {
        return false;
    }

    return now - lastFetchAt < minIntervalMs;
};
